import { useCallback, useContext, useMemo } from "react";
import { ButtonWithLoader } from "../general/ButtonWithLoader/ButtonWithLoader";
import { useIntegrationAccountLinkMutation } from "../../api/integrationAccount.api";
import { ImporterContext } from "./importer.context";
import { isRowReadyForImport } from "./helpers";
import styles from "./ImporterPage.module.scss";
import { ImporterDocumentImportStatus, ImporterDocumentRow } from "./types";

export const ImporterPageFooter: React.FC = () => {
    const { documentRows, updateDocumentRow } = useContext(ImporterContext);

    const rowsReadyForImport = useMemo(
        () => documentRows.filter(isRowReadyForImport),
        [documentRows],
    );

    const linkAccount = useIntegrationAccountLinkMutation();

    const importNextDocument = useCallback(
        async (rowsForImport: ImporterDocumentRow[]) => {
            const nextRow = rowsForImport.shift();

            if (!nextRow) {
                return;
            }

            if (nextRow.integrationAccount) {
                updateDocumentRow(nextRow.id, {
                    importStatus: ImporterDocumentImportStatus.IN_PROGRESS,
                });
                await linkAccount.mutateAsync(nextRow.integrationAccount);
                updateDocumentRow(nextRow.id, {
                    importStatus: ImporterDocumentImportStatus.FINISHED,
                });
            }

            await importNextDocument(rowsForImport);
        },
        [linkAccount, updateDocumentRow],
    );

    const importDocuments = useCallback(() => {
        rowsReadyForImport.forEach((row) => {
            updateDocumentRow(row.id, {
                importStatus: ImporterDocumentImportStatus.SCHEDULED,
            });
        });

        importNextDocument(rowsReadyForImport);
    }, [importNextDocument, rowsReadyForImport, updateDocumentRow]);

    if (!rowsReadyForImport.length) {
        return null;
    }

    return (
        <div className={styles.footer}>
            <ButtonWithLoader
                onClick={importDocuments}
                loading={linkAccount.isPending}
            >
                Import documents{" "}
                <span className={styles.importDocumentsNumber}>
                    {rowsReadyForImport.length}
                </span>
            </ButtonWithLoader>
        </div>
    );
};
